// App routes configuration - v2 landing page with Sentry monitoring
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SubscriptionProvider, useSubscription } from "@/hooks/useSubscription";
import ProtectedRoute from "@/components/ProtectedRoute";

import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useSentryUser, addNavigationBreadcrumb } from "@/utils/sentry";
import NewLanding from "./pages/NewLanding";
import NotFound from "./pages/NotFound";
import InstagramSetup from "./pages/onboarding/InstagramSetup";
import IndustrySelection from "./pages/onboarding/IndustrySelection";
import CompetitorAnalysis from "./pages/onboarding/CompetitorAnalysis";
import AddCompetitors from "./pages/onboarding/AddCompetitors";

import Dashboard from "./pages/Dashboard";
import Library from "./pages/Library";
import Favorites from "./pages/Favorites";
import Analytics from "./pages/Analytics";
import Competitors from "./pages/Competitors";
import Inspiration from "./pages/Inspiration";
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import IdeaGenerator from "./pages/IdeaGenerator";
import Edytor from "./pages/Edytor";
import Prompter from "./pages/Prompter";
import Realizacja from "./pages/Realizacja";
import Kontakt from "./pages/Kontakt";
import Konsultacja from "./pages/Konsultacja";
import Faq from "./pages/Faq";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import BlogHooki from "./pages/BlogHooki";
import Pomoc from "./pages/Pomoc";
import ResetPassword from "./pages/ResetPassword";
import Subscribe from "./pages/Subscribe";
import ScrollToTop from "./components/ScrollToTop";
import { AuthHashHandler } from "./components/AuthHashHandler";
import { MaintenanceBanner } from "./components/MaintenanceBanner";
import Loader2 from "./pages/Loader2";
import Academy from "./pages/Academy";
import PolitykaPrywatnosci from "./pages/PolitykaPrywatnosci";
import Regulamin from "./pages/Regulamin";

// Configure React Query with Sentry-friendly defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && error.message.includes('4')) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Sentry User Context Provider
 * Sets Sentry user context when auth state changes
 */
function SentryContextProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { tier } = useSubscription();
  const location = useLocation();

  // Set Sentry user context
  useSentryUser(
    user?.id,
    user?.email,
    tier
  );

  // Track navigation for breadcrumbs
  useEffect(() => {
    addNavigationBreadcrumb('previous', location.pathname);
  }, [location.pathname]);

  return <>{children}</>;
}

/**
 * Route wrapper with ErrorBoundary for critical pages
 */
function CriticalRoute({ children, context }: { children: React.ReactNode; context?: Record<string, unknown> }) {
  const { user } = useAuth();
  
  return (
    <ErrorBoundary 
      userId={user?.id} 
      userEmail={user?.email}
      context={context}
    >
      {children}
    </ErrorBoundary>
  );
}

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="viraluj-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />

          <BrowserRouter>
            <ScrollToTop />
            <AuthHashHandler />
            <SentryContextProvider>
              <MaintenanceBanner />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<NewLanding />} />
                <Route path="/realizacja" element={<Realizacja />} />
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/konsultacja" element={<Konsultacja />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/jak-zaczac-nagrywac-wideo-do-internetu" element={<BlogArticle />} />
                <Route path="/blog/hooki-w-social-media" element={<BlogHooki />} />
                <Route path="/pomoc" element={<Pomoc />} />
                <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosci />} />
                <Route path="/regulamin" element={<Regulamin />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/loader2" element={<Loader2 />} />
                
                {/* Dashboard routes — shared layout (sidebar + header rendered once) */}
                <Route element={
                  <ProtectedRoute>
                    <CriticalRoute>
                      <DashboardLayout />
                    </CriticalRoute>
                  </ProtectedRoute>
                }>
                  <Route path="/panel" element={<Dashboard />} />
                  <Route path="/inspiracje" element={<Inspiration />} />
                  <Route path="/konkurencja" element={<Competitors />} />
                  <Route path="/biblioteka" element={<Library />} />
                  <Route path="/ulubione" element={<Favorites />} />
                  <Route path="/analityka" element={<Analytics />} />
                  <Route path="/kreator-scenariusza" element={<IdeaGenerator />} />
                  <Route path="/ustawienia" element={<Settings />} />
                  <Route path="/edytor" element={<Edytor />} />
                  <Route path="/akademia" element={<Academy />} />
                </Route>

                {/* Protected routes without dashboard layout */}
                <Route path="/subscribe" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'subscription', flow: 'checkout' }}>
                      <Subscribe />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                
                <Route path="/onboarding/instagram" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'onboarding', step: 'instagram' }}>
                      <InstagramSetup />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                
                <Route path="/onboarding/industry" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'onboarding', step: 'industry' }}>
                      <IndustrySelection />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                
                <Route path="/onboarding/inspiration-sources" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'onboarding', step: 'competitors' }}>
                      <CompetitorAnalysis />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                
                <Route path="/onboarding/add-competitors" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'onboarding', step: 'add_competitors' }}>
                      <AddCompetitors />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                
                <Route path="/prompter" element={
                  <ProtectedRoute>
                    <CriticalRoute context={{ feature: 'content_generation', tool: 'prompter' }}>
                      <Prompter />
                    </CriticalRoute>
                  </ProtectedRoute>
                } />
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </SentryContextProvider>
          </BrowserRouter>
          </TooltipProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
